/**
 * @description Merchant transaction API client.
 * @dependencies apiClient
 * @notes PUT /transactions/:id may wrap the DTO in { success, data }. Always
 * unwrap before setState or formatCurrency(undefined) renders $0.00.
 */
import apiClient from './apiClient';

/**
 * PUT /transactions/:id historically wraps the DTO in { success, data }.
 * GET /transactions/id/:id returns the DTO. Assigning the wrapper to React
 * state makes amount undefined → formatCurrency renders $0.00.
 * @param {import('axios').AxiosResponse} response
 * @returns {object}
 */
function unwrapTransaction(response) {
  const body = response?.data;
  if (body && typeof body === 'object' && body.data && (body.success === true || body.message)) {
    return body.data;
  }
  return body;
}

/**
 * Transaction service for handling transaction-related API calls
 */
const transactionsService = {
  /**
   * Get a specific transaction by ID
   * @param {string} id - Transaction ID
   * @returns {Promise} API response
   */
  getTransaction: async (id) => {
    if (!id) {
      throw new Error('Transaction ID is required');
    }
    
    const response = await apiClient.get(`/transactions/id/${id}`);
    return response.data;
  },
  
  /**
   * Get all transactions for current authenticated user
   * @returns {Promise} API response
   */
  getMerchantTransactions: async () => {
    // Session-based: uses authenticated user from req.user
    const response = await apiClient.get('/transactions');
    return response.data;
  },
  
  /**
   * Get transactions for a specific customer
   * @param {string} customerId - Customer ID
   * @returns {Promise} API response
   */
  getCustomerTransactions: async (customerId) => {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }
    
    const response = await apiClient.get(`/transactions/customer/${customerId}`);
    return response.data;
  },
  
  /**
   * Create a mock transaction
   * @param {Object} data - Transaction data
   * @returns {Promise} API response
   */
  createMockTransaction: async (data) => {
    if (!data.userId) {
      throw new Error('User ID is required');
    }
    
    const response = await apiClient.post('/transactions/mock-transaction', data);
    return response.data;
  },
  
  /**
   * Get AVS response text description
   * @param {string} code - AVS response code
   * @returns {string} Human-readable description
   */
  getAvsResponseText: (code) => {
    const avsResponses = {
      'Y': 'Address and ZIP match',
      'A': 'Address matches, ZIP does not',
      'Z': 'ZIP matches, Address does not',
      'N': 'Neither ZIP nor Address match',
      'U': 'Address info unavailable',
      'R': 'System unavailable, retry',
      'E': 'Not eligible for AVS',
      'S': 'Not supported',
      'D': 'International match',
      'M': 'Street and Postal Code match',
      'B': 'Address matches (Street only)',
      'P': 'Postal Code matches'
    };
    return avsResponses[code] || 'Not Available';
  },
  

  /**
   * Update a transaction
   * @param {string} id - Transaction ID
   * @param {Object} data - Update data
   * @returns {Promise} API response
   */
  updateTransaction: async (id, data) => {
    if (!id) {
      throw new Error('Transaction ID is required');
    }
    
    const response = await apiClient.put(`/transactions/${id}`, data);
    return unwrapTransaction(response);
  }
};

export default transactionsService;